import React,{useRef,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {Card,PageTitle,Status} from '../components/Shared';
import {exportLocalData,importLocalData} from '../storage';

const integrations=[
  ['OpenAI API','回答分析・メール文案生成',FiIcons.FiCpu],
  ['Zoom','出席情報・ウェビナー連携',FiIcons.FiVideo],
  ['Calendly / Google Calendar','商談予約',FiIcons.FiCalendar],
  ['Slack','営業担当者への通知',FiIcons.FiMessageSquare],
  ['Google Sheets','顧客データ出力',FiIcons.FiGrid],
  ['メール配信サービス','ステップメール配信',FiIcons.FiMail],
  ['n8n','追加ワークフロー',FiIcons.FiGitBranch]
];

function downloadJson(data,filename) {
  const blob=new Blob([JSON.stringify(data,null,2)],{type:'application/json'});
  const link=document.createElement('a');
  link.href=URL.createObjectURL(blob);
  link.download=filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function Settings() {
  const [bookingUrl,setBookingUrl]=useState(localStorage.getItem('awsa_booking_url') || '');
  const [saved,setSaved]=useState(false);
  const [message,setMessage]=useState('');
  const [error,setError]=useState('');
  const fileInput=useRef(null);

  const saveUrl=event=>{
    event.preventDefault();
    localStorage.setItem('awsa_booking_url',bookingUrl);
    setSaved(true);
    setTimeout(()=>setSaved(false),2500);
  };

  const exportData=()=>{
    downloadJson(exportLocalData(),'webinarflow-local-backup.json');
    setMessage('全データのバックアップをダウンロードしました。');
    setError('');
  };

  const importData=async event=>{
    const file=event.target.files?.[0];
    if (!file) return;
    try {
      const counts=importLocalData(await file.text());
      const added=Object.values(counts).reduce((sum,value)=>sum+value,0);
      setMessage(`${added}件を追加しました。同じ顧客メールアドレスと既存IDのデータは重複追加していません。`);
      setError('');
    } catch (reason) {
      setError(reason.message || 'バックアップを読み込めませんでした。既存データは変更していません。');
      setMessage('');
    } finally {
      event.target.value='';
    }
  };

  const reset=()=>{
    if (!window.confirm('初期化前に、現在のローカルデータをバックアップとしてダウンロードします。続けますか？')) return;
    downloadJson(exportLocalData(),'webinarflow-before-reset.json');
    if (!window.confirm('バックアップを保存しました。全データを初期化してデモ状態に戻しますか？')) return;
    ['awsa_contacts','awsa_webinars','awsa_sessions','awsa_registrations','awsa_deliveries',
      'awsa_workflows','awsa_logs'].forEach(key=>localStorage.removeItem(key));
    window.location.reload();
  };

  return <div className="page-stack">
    <PageTitle title="連携・設定" subtitle="外部サービスの接続状況と、ローカルデータの管理を行います。" />
    <div className="notice-banner warning-banner"><SafeIcon icon={FiIcons.FiShield} />
      <span>管理者認証・権限管理・共有データベースは未実装です。現在の保存先はブラウザーのlocalStorageです。</span>
    </div>
    <Card><div className="card-heading"><div><h3>外部サービス連携</h3>
      <p>APIキーはブラウザーに保存しないでください。安全なサーバー連携が必要です。</p></div>
      <Status tone="warning">すべて未接続</Status></div>
      <div className="integration-grid">{integrations.map(([name,description,icon])=>
        <div className="integration-item" key={name}>
          <span className="integration-icon"><SafeIcon icon={icon} /></span>
          <div><b>{name}</b><small>{description}</small></div>
          <Status tone="muted">未接続</Status>
        </div>
      )}</div>
    </Card>
    <Card><div className="card-heading"><div><h3>商談予約URL</h3>
      <p>予約サービス未接続時は、外部URLへ案内するための設定値です。</p></div></div>
      <form className="booking-form" onSubmit={saveUrl}>
        <label>Calendly等の公開予約URL<input type="url" value={bookingUrl}
          onChange={event=>setBookingUrl(event.target.value)} placeholder="https://calendly.com/your-team/meeting" /></label>
        <button className="primary-button">保存</button>
        {saved && <Status tone="success">保存しました</Status>}
      </form>
    </Card>
    <Card><div className="card-heading"><div><h3>データのバックアップ・移行</h3>
      <p>顧客マスター、開催回、申込履歴、配信予定、ワークフロー、ログをJSONで扱います。</p></div></div>
      <div className="notice-banner"><SafeIcon icon={FiIcons.FiInfo} />
        <span>移行前にエクスポートしてください。読み込みは追加方式で、既存レコードを上書き・削除しません。顧客はメールアドレス、その他はIDを基準に重複を避けます。</span>
      </div>
      {message && <p className="muted-copy">{message}</p>}
      {error && <div className="form-error"><SafeIcon icon={FiIcons.FiAlertCircle} />{error}</div>}
      <div className="data-actions"><div><b>全データをエクスポート</b>
        <small>ローカルデータ全体の移行用バックアップを作成します。</small></div>
        <button className="secondary-button" onClick={exportData}><SafeIcon icon={FiIcons.FiDownload} />JSONをダウンロード</button>
      </div>
      <div className="data-actions"><div><b>バックアップを追加読み込み</b>
        <small>別環境へ移行するときに利用します。元データは保持されます。</small></div>
        <button className="secondary-button" onClick={()=>fileInput.current?.click()}>
          <SafeIcon icon={FiIcons.FiUpload} />JSONを読み込む
        </button>
        <input ref={fileInput} type="file" accept="application/json,.json" onChange={importData} hidden />
      </div>
      <div className="data-actions danger-action"><div><b>デモ・ローカルデータを初期化</b>
        <small>実行前にバックアップをダウンロードし、確認を2回行います。</small></div>
        <button className="danger-button" onClick={reset}><SafeIcon icon={FiIcons.FiTrash2} />データを初期化</button>
      </div>
    </Card>
    <Card className="privacy-card"><span className="side-icon"><SafeIcon icon={FiIcons.FiLock} /></span>
      <div><h3>デモ利用と将来の連携</h3>
        <p>公開申込フォームは架空のテスト情報（example.jpメールアドレス）のみ受け付けます。実際のメール送信や外部サービス連携は行いません。Google Sheets等へ移行する際は、バックアップを保管したうえで連携先の列構成とID対応を確認してから移行してください。既存のローカルデータが自動で移行されることはありません。</p>
      </div>
    </Card>
  </div>;
}
